// src/lib/contentNormalizer.js
// Filepath: // src/lib/contentNormalizer.js
/* eslint-disable no-console */

/**
 * Shared normalization helpers for rails (Home).
 *
 * - Derive playable/visual URLs from `artUrl` based on expectedMediaType.
 * - Keep expectedMediaType CASE SENSITIVE: "Video" | "Image".
 * - We never claim "video" without a usable URL; images are always safe.
 */

export const RAIL_TITLE_FREE = "Featured";
export const RAIL_TITLE_PURCHASES = "My Purchases";
export const RAIL_TITLE_COLLECTIONS = "My Collections";

/** ───────────────────────────── Debug toggles ───────────────────────────── */
const ENV_DEBUG =
  String(process.env.REACT_APP_DEBUG_RAILS || "")
    .trim()
    .toLowerCase() === "true";

let runtimeDebug = null; // can be toggled via setRailsDebug(true|false)

/** Prefer explicit runtime toggle, then window flag, then env */
function isDebug() {
  if (runtimeDebug != null) return runtimeDebug;
  try {
    if (typeof window !== "undefined" && typeof window.__KBEAN_DEBUG_RAILS === "boolean") {
      return window.__KBEAN_DEBUG_RAILS;
    }
  } catch {}
  return ENV_DEBUG;
}

/** Allow code to toggle debug at runtime (e.g., from DevTools or a debug menu) */
export function setRailsDebug(enabled) {
  runtimeDebug = !!enabled;
}

/** Expose debug-enabled state for external components (HomeScene/GridScene) */
export function railsDebugEnabled() {
  return isDebug();
}

/** Pretty-select fields we actually care about in logs */
function pickLogFields(item) {
  if (!item || typeof item !== "object") return item;
  const {
    id,
    title,
    subtitle,
    description,
    expectedMediaType,
    isVideo,
    mediaUrl,
    previewUrl,
    imageUrl,
    durationSec,
    backgroundUrl,
    thumbnailUrl,
    byline,
    // NEW hero-friendly fields for visibility
    medium,
    allowWallpaper,
    sharesYouOwn,
    valueYouOwn,
    type,
  } = item;
  return {
    id,
    title,
    subtitle,
    description,
    expectedMediaType,
    isVideo,
    mediaUrl,
    previewUrl,
    imageUrl,
    durationSec,
    backgroundUrl,
    thumbnailUrl,
    byline,
    medium,
    allowWallpaper,
    sharesYouOwn,
    valueYouOwn,
    type,
  };
}

function logNormalizedItem(kind, srcIndex, normalized) {
  if (!isDebug()) return;
  console.groupCollapsed(
    `[normalize:${kind}] #${srcIndex} ${normalized?.title || normalized?.id || ""}`
  );
  console.log(pickLogFields(normalized));
  console.groupEnd();
}

function logRailsSummary(rails) {
  if (!isDebug()) return;
  try {
    console.groupCollapsed(
      `[rails] built ${Array.isArray(rails) ? rails.length : 0} rail(s)`
    );
    const rows = (rails || []).map((r, i) => ({
      idx: i,
      title: r?.title,
      count: Array.isArray(r?.items) ? r.items.length : 0,
      firstItem: r?.items?.[0] ? pickLogFields(r.items[0]) : null,
    }));
    console.table(rows);
    console.groupEnd();
  } catch {
    // no-op
  }
}

/** ───────────────────────────── Core helpers ───────────────────────────── */

/** Safe join even if artUrl has a trailing slash */
function join(u, suffix) {
  if (!u) return "";
  const base = String(u).replace(/\/+$/, "");
  return `${base}/${suffix}`;
}

/** Best-effort description extraction from raw payloads */
function pickRawDescription(raw) {
  return (
    raw?.description ??
    raw?.artDescription ??
    raw?.summary ??
    raw?.desc ??
    raw?.details?.description ??
    raw?.metadata?.description ??
    raw?.caption ??
    raw?.about ??
    null
  );
}

/**
 * Derive all asset URLs from a base `artUrl` + `expectedMediaType`.
 * For Video:
 *   - videoUrl: "_main.mp4"
 *   - previewUrl: "preview.mp4"
 *   - thumbnailUrl: "_thumb_large.jpg"
 *   - backgroundUrl: "thumbnail_master.png"   (hero bg)
 * For Image:
 *   - imageUrl: "_main.jpg"
 *   - thumbnailUrl: "_thumb_large.jpg"
 *   - backgroundUrl: "_main.jpg"              (hero bg)
 */
export function deriveAssetsFromArtUrl(artUrl, expectedMediaType) {
  const type = expectedMediaType === "Video" ? "Video" : "Image";

  if (type === "Video") {
    return {
      videoUrl: join(artUrl, "_main.mp4"),
      previewUrl: join(artUrl, "preview.mp4"),
      thumbnailUrl: join(artUrl, "_thumb_large.jpg"),
      backgroundUrl: join(artUrl, "thumbnail_master.png"),
      imageUrl: null,
    };
  }

  // Image
  return {
    imageUrl: join(artUrl, "_main.jpg"),
    thumbnailUrl: join(artUrl, "_thumb_large.jpg"),
    backgroundUrl: join(artUrl, "_main.jpg"),
    videoUrl: null,
    previewUrl: null,
  };
}

function normalizeCommonFields(raw) {
  const title =
    raw?.title ||
    raw?.name ||
    [raw?.artistFirstName, raw?.artistLastName].filter(Boolean).join(" ") ||
    "Untitled";

  const expectedMediaType =
    raw?.expectedMediaType === "Video" ? "Video" : "Image";

  const id =
    raw?.artworkId ||
    raw?.id ||
    raw?._id ||
    `${Math.random().toString(36).slice(2, 10)}`;

  return { id, title, expectedMediaType };
}

/**
 * Normalize a Free artwork (from getFreeContent() -> collections[].artworksList[])
 * into a card/item for Home rails and Item/Gallery scenes.
 */
export function normalizeFreeArtwork(artwork, _idx = 0) {
  const { id, title, expectedMediaType } = normalizeCommonFields(artwork);
  const artUrl = artwork?.artUrl || "";
  const assets = deriveAssetsFromArtUrl(artUrl, expectedMediaType);

  const isVideo = expectedMediaType === "Video";
  const description = pickRawDescription(artwork);

  // NEW: derive hero-friendly meta
  const artist =
    [artwork?.artistFirstName, artwork?.artistLastName].filter(Boolean).join(" ") || null;
  const year = artwork?.year || artwork?.artYear || null;
  const byline = [artist, year].filter(Boolean).join(" • ") || null;
  const medium = artwork?.medium || null;

  const normalized = {
    id,
    title,
    subtitle: description ?? null,        // subtitle is the art description
    description: description ?? null,     // explicit description field for consumers
    expectedMediaType,                    // "Video" | "Image"
    isVideo,
    mediaUrl: isVideo ? assets.videoUrl : null,   // ONLY when Video
    previewUrl: isVideo ? assets.previewUrl : null,
    imageUrl: !isVideo ? assets.imageUrl : null,
    durationSec: artwork?.durationSec ?? null,
    backgroundUrl: assets.backgroundUrl,
    thumbnailUrl: assets.thumbnailUrl,
    byline,                               // "Artist • Year"
    medium,                               // e.g. "Oil on canvas"
    allowWallpaper: !!artwork?.allowWallpaper,
    sharesYouOwn: Number(artwork?.sharesYouOwn || 0),
    valueYouOwn: artwork?.valueYouOwn ?? null,
    _raw: artwork,
  };

  logNormalizedItem("free", _idx, normalized);
  return normalized;
}

/**
 * Normalize a Holding (from getMyHoldings().holdings[]) to a card/item.
 */
export function normalizeHolding(holding, _idx = 0) {
  const { id, title, expectedMediaType } = normalizeCommonFields(holding);
  const artUrl = holding?.artUrl || "";
  const assets = deriveAssetsFromArtUrl(artUrl, expectedMediaType);
  const isVideo = expectedMediaType === "Video";

  const artist =
    [holding?.artistFirstName, holding?.artistLastName].filter(Boolean).join(" ") || null;
  const year = holding?.artYear || holding?.year || null;
  const byline = [artist, year].filter(Boolean).join(" • ") || null;

  const description = pickRawDescription(holding);
  const medium = holding?.medium || null;

  const normalized = {
    id,
    title,
    subtitle: description ?? null,
    description: description ?? null,
    expectedMediaType,                    // "Video" | "Image"
    isVideo,
    mediaUrl: isVideo ? assets.videoUrl : null,   // ONLY when Video
    previewUrl: isVideo ? assets.previewUrl : null,
    imageUrl: !isVideo ? assets.imageUrl : null,
    durationSec: null,                    // holdings don't provide duration
    backgroundUrl: assets.backgroundUrl,
    thumbnailUrl: assets.thumbnailUrl,
    byline,
    medium,
    allowWallpaper: !!holding?.allowWallpaper,
    sharesYouOwn: Number(holding?.sharesYouOwn || 0),
    valueYouOwn: holding?.valueYouOwn ?? null,
    _raw: holding,
  };

  logNormalizedItem("holding", _idx, normalized);
  return normalized;
}

/**
 * Build a single rail from Free collections payload.
 * Input shape: Array<{ collectionId, collectionName, artworksList: [...] }>
 * We flatten all artworks into one rail.
 */
export function buildFreeRail(freeCollections) {
  const arr = Array.isArray(freeCollections) ? freeCollections : [];
  const artworks = arr.flatMap((c) =>
    Array.isArray(c?.artworksList) ? c.artworksList : []
  );
  const items = artworks.map((aw, i) => normalizeFreeArtwork(aw, i));

  if (!items.length) return null;
  return { title: RAIL_TITLE_FREE, items };
}

/**
 * Build a single aggregated rail from My Holdings payload.
 * Input shape: { total..., holdings: [...] }
 */
export function buildPurchasesRail(myHoldingsResponse) {
  const holdings = Array.isArray(myHoldingsResponse?.holdings)
    ? myHoldingsResponse.holdings
    : [];

  const items = holdings.map((h, i) => normalizeHolding(h, i));
  if (!items.length) return null;

  return { title: RAIL_TITLE_PURCHASES, items };
}

/**
 * Build rails per user profile collection, using ownedArtwork IDs to find holdings.
 * (Kept for fidelity/back-compat; not added by default in buildRails.)
 */
export function buildPurchasesRailsByCollections(userProfile, myHoldingsResponse) {
  const collections = Array.isArray(userProfile?.collections)
    ? userProfile.collections
    : [];
  const holdings = Array.isArray(myHoldingsResponse?.holdings)
    ? myHoldingsResponse.holdings
    : [];

  if (!collections.length || !holdings.length) return [];

  const byId = new Map();
  for (const h of holdings) {
    if (h?.artworkId) byId.set(String(h.artworkId), h);
  }

  const rails = [];
  for (const col of collections) {
    const owned = Array.isArray(col?.ownedArtworks) ? col.ownedArtworks : [];
    const items = [];
    for (const artId of owned) {
      const h = byId.get(String(artId));
      if (h) items.push(normalizeHolding(h));
    }
    if (items.length) {
      const title =
        (col?.collectionName && String(col.collectionName).trim()) || "Collection";
      rails.push({ title, items });
    }
  }
  return rails;
}

/** ───────────────── My Collections (tiles) + item selector ─────────────── */

/** Build an index of normalized purchases by id for quick lookups */
function purchasesIndex(myHoldingsResponse) {
  const rail = buildPurchasesRail(myHoldingsResponse);
  const items = Array.isArray(rail?.items) ? rail.items : [];
  const byId = new Map();
  for (const it of items) {
    if (it?.id != null) byId.set(String(it.id), it);
  }
  return { items, byId };
}

/**
 * Build a single "My Collections" rail where each tile represents a collection.
 * Cover = thumbnail from the first owned artwork in the collection.
 */
export function buildCollectionsRail(userProfile, myHoldingsResponse) {
  const collections = Array.isArray(userProfile?.collections)
    ? userProfile.collections
    : [];
  if (!collections.length) return null;

  const { byId } = purchasesIndex(myHoldingsResponse);

  const items = collections.map((col, idx) => {
    const id = col?.id || col?._id || `collection-${idx}`;
    const title =
      (col?.collectionName && String(col.collectionName).trim()) || "Collection";

    const ownedIds = Array.isArray(col?.ownedArtworks)
      ? col.ownedArtworks.map(String)
      : [];

    const firstItem =
      ownedIds.length > 0 ? byId.get(String(ownedIds[0])) || null : null;

    const coverThumb =
      firstItem?.thumbnailUrl ||
      firstItem?.imageUrl ||
      firstItem?.backgroundUrl ||
      null;

    const coverBg = firstItem?.backgroundUrl || coverThumb || null;

    const normalizedCollectionTile = {
      id: String(id),
      title,
      type: "collection",
      expectedMediaType: "Image",
      isVideo: false,
      mediaUrl: null,
      previewUrl: null,
      imageUrl: null,
      durationSec: null,
      backgroundUrl: coverBg,
      thumbnailUrl: coverThumb,
      byline: null,
      _ownedArtIds: ownedIds,
      _raw: col,
    };

    logNormalizedItem("collectionTile", idx, normalizedCollectionTile);
    return normalizedCollectionTile;
  });

  if (!items.length) return null;
  return { title: RAIL_TITLE_COLLECTIONS, items };
}

/**
 * Resolve the normalized items for a given collection id.
 * Returns the already-normalized purchase items in collection order,
 * filtered to the collection’s ownedArtwork IDs.
 */
export function selectCollectionItems(collectionId, userProfile, myHoldingsResponse) {
  const idStr = String(collectionId ?? "");
  const collections = Array.isArray(userProfile?.collections)
    ? userProfile.collections
    : [];
  const match = collections.find((c) => String(c?.id ?? c?._id) === idStr);
  if (!match) return [];

  const ownedIds = Array.isArray(match?.ownedArtworks)
    ? match.ownedArtworks.map(String)
    : [];
  if (!ownedIds.length) return [];

  const { byId } = purchasesIndex(myHoldingsResponse);

  const items = ownedIds
    .map((aid) => byId.get(aid) || null)
    .filter(Boolean);

  if (railsDebugEnabled()) {
    try {
      console.groupCollapsed(`[collections] items for ${idStr}: ${items.length}`);
      console.table(
        items.map((i, idx) => ({
          idx,
          id: i.id,
          title: i.title,
          type: i.expectedMediaType,
          mediaUrl: i.mediaUrl,
          thumb: i.thumbnailUrl,
        }))
      );
      console.groupEnd();
    } catch {}
  }

  return items;
}

/** ────────────────────────────── buildRails ─────────────────────────────── */
export function buildRails({ freeCollections, myHoldings, userProfile } = {}) {
  const rails = [];

  const freeRail = buildFreeRail(freeCollections);
  if (freeRail) rails.push(freeRail);

  const purchasesRail = buildPurchasesRail(myHoldings);
  if (purchasesRail) rails.push(purchasesRail);

  const collectionsRail = buildCollectionsRail(userProfile, myHoldings);
  if (collectionsRail) rails.push(collectionsRail);

  logRailsSummary(rails);
  return rails;
}
